/**
 * Emit hosts for the `ui` analytics group — 27 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { ScreenEventProps, TeamInviteSource } from '@/models/analytics/features/ui';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { isOnPrem } from '@/utils/buildProfile';
import { type Period } from '@heidi/commerce/contracts';
import {
  ButtonAction,
  type FieldType,
  ScreenName,
} from '@heidi/runtime/analytics-vocabulary';
import { type SubscriptionTier } from '@heidi/schema/ml-scribe';

export function endTrialModalClosed() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalClosed,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'close',
    },
  });
}

export function endTrialModalDowngradeClicked() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalDowngradeClicked,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'downgrade',
    },
  });
}

export function endTrialModalOpened() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalOpened,
    },
  });

  // V2 — modal_viewed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
    },
  });
}

export function endTrialModalPeriodChanged(input: { period: Period }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalPeriodChanged,
      period: input.period,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'period_changed',
      period: input.period,
    },
  });
}

export function endTrialModalUpgradeClicked(input: { period: Period }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.EndTrialModalUpgradeClicked,
      period: input.period,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'upgrade',
      period: input.period,
    },
  });
}

export function extendTrialModalBackClicked(input: { selectedHours: number }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.ExtendTrialModalBackClicked,
      selectHours: input.selectedHours,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.TrialExtensionModal,
      label: 'back',
      selected_hours: input.selectedHours,
    },
  });
}

export function extendTrialModalExtendClicked(input: { selectedHours: number }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.ExtendTrialModalExtendClicked,
      selectHours: input.selectedHours,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.TrialExtensionModal,
      label: 'extend',
      selected_hours: input.selectedHours,
    },
  });
}

// Extend Trial Modal
export function extendTrialModalOpened() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.ExtendTrialModalOpened,
    },
  });

  // V2 — modal_viewed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.TrialExtensionModal,
    },
  });
}

export function settingsBillingCurrentPlanUpgradeClicked(currentPlan: SubscriptionTier) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      label: 'settings_billing_current_plan_upgrade',
      action: ButtonAction.SettingsBillingCurrentPlanUpgrade,
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      current_plan: currentPlan,
    },
  });
}

export function settingsBillingEnterpriseGetInTouchClicked(
  currentPlan: SubscriptionTier
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      label: 'settings_billing_enterprise_get_in_touch',
      action: ButtonAction.SettingsBillingEnterpriseGetInTouch,
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      current_plan: currentPlan,
    },
  });
}

export function settingsBillingTrialUpgradeNowClick(input: {
  current_plan: SubscriptionTier;
}) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      action: ButtonAction.SettingsBillingTrialUpgradeNowClicked,
      label: 'upgrade_now',
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      current_plan: input.current_plan,
    },
  });
}

export function settingsManageBillingClicked(input?: {
  current_plan?: SubscriptionTier;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Settings,
    props: {
      action: SegmentActionName.SettingsManageBillingClicked,
    },
  });

  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      label: 'settings_billing_manage_billing',
      action: ButtonAction.SettingsBillingManageBilling,
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      ...(input?.current_plan !== undefined ? { current_plan: input.current_plan } : {}),
    },
  });
}

export function settingsTrialBillingCancelTrialClick(input: {
  current_plan: SubscriptionTier;
}) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      action: ButtonAction.SettingsBillingCancelTrial,
      label: 'cancel_trial',
      feature: 'billing',
      screen_name: ScreenName.SettingsBilling,
      current_plan: input.current_plan,
    },
  });
}

export function templateCreateNewClicked() {
  // Intent signal only — the authoritative `template_created` fires at save time.
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateCreateNewClicked,
    },
  });
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      action: ButtonAction.TemplateCreateNew,
      label: 'Create new template',
    },
  });
}

export function templateCreateUseExistingClicked() {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateCreatedUseExistingClicked,
    },
  });
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.TemplateLibrary,
      action: ButtonAction.TemplateUseExisting,
      label: 'Use existing template',
    },
  });
}

export function templateCreateUseScratchClicked() {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplateCreatedUseScratchClicked,
    },
  });
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.TemplateLibrary,
      action: ButtonAction.TemplateStartFromScratch,
      label: 'Start from scratch',
    },
  });
}

export function trackCreateNoteOrDocumentClicked() {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.ChooseTemplateTypeModal,
      action: ButtonAction.CreateNoteOrDocument,
      label: 'Note or document',
    },
  });
}

export function trackFieldSelected(input: {
  fieldName: string;
  fieldType: FieldType;
  screenName: ScreenName;
  screenSection?: string;
  /** Wizard step slug (e.g. dictate onboarding). */
  stepName?: string;
  // Selected value, for bounded non-PII fields (e.g. the onboarding
  // EMR/EHR and specialty dropdowns). Never pass for free-text fields —
  // see the field_value note on FieldSelectedEventProps.
  fieldValue?: string;
  // Funnel-stitching for the patient find-or-link combobox; nullable on
  // every other field_selected caller (template fields, etc.). See the
  // generated FieldSelected schema (v1-1-0) for the contract.
  searchId?: string;
  featureFlagVariant?: 'treated' | 'control' | 'unknown';
  country?: string;
  language?: string;
  // Destination control passthrough. Omitted on the pre-policy call sites
  // (Amplitude stays ON by default for them); new call sites must pass
  // `{ amplitude: false }` per the Snowplow-first routing policy.
  options?: { segment?: boolean; posthog?: boolean; amplitude?: boolean };
}) {
  track({
    event_name: TrackingEventName.FieldSelected,
    props: {
      field_name: input.fieldName,
      field_type: input.fieldType,
      screen_name: input.screenName,
      ...(input.screenSection && { screen_section: input.screenSection }),
      ...(input.stepName && { step_name: input.stepName }),
      ...(input.fieldValue && { field_value: input.fieldValue }),
      ...(input.searchId && { search_id: input.searchId }),
      ...(input.featureFlagVariant && {
        feature_flag_variant: input.featureFlagVariant,
      }),
      ...(input.country && { country: input.country }),
      ...(input.language && { language: input.language }),
    },
    ...(input.options && { options: input.options }),
  });
}

export function trackFindReplaceModalCancelled() {
  trackLegacyEvent({
    name: SegmentObjectName.GlobalConfig,
    props: {
      action: SegmentActionName.FindReplaceModalCancelled,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.FindReplaceModal,
    },
  });
}

// global configuration settings
export function trackFindReplaceModalOpened() {
  trackLegacyEvent({
    name: SegmentObjectName.GlobalConfig,
    props: {
      action: SegmentActionName.FindReplaceModalOpened,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.FindReplaceModal,
    },
  });
}

export function trackModalViewed(input: { screenName: ScreenName }) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: input.screenName,
    },
  });
}

// UI. V2 Events
export function trackScreenViewed(props: ScreenEventProps) {
  track({
    event_name: TrackingEventName.ScreenViewed,
    props,
  });
}

export function trackSendTeamInvitation({
  teamId,
  email,
  teamName,
  teamLink,
  totalEmailCount,
  source,
}: {
  teamId: string;
  email: string;
  teamName: string;
  teamLink?: string;
  totalEmailCount?: number;
  source?: TeamInviteSource;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Team,
    props: {
      action: SegmentActionName.SendTeamInvitation,
      teamId: teamId,
      teamName: teamName,
      ...(isOnPrem ? {} : { invitedEmail: email }),
      teamLink: teamLink,
      ...(totalEmailCount !== undefined && { totalEmailCount }),
      ...(source && { source }),
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TeamMemberCreated,
    props: {
      team_id: teamId,
      // PII redaction (OBS-1.C4): email stripped on on-prem — defense-in-depth;
      // the collector blacklist catches "email" exactly, but on-prem must never
      // emit PII regardless. Cloud retains existing behavior.
      ...(isOnPrem ? {} : { email: email }),
      creation_action: 'send_invitation',
      team_name: teamName,
      team_link: teamLink,
      total_email_count: totalEmailCount,
      source: source,
    },
  });
}

export function trialToPaidModalClosed() {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.TrialToPaidModalClosed,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'close',
    },
  });
}

export function trialToPaidModalOpened(params: {
  location: 'cta-button' | 'account-form' | 'quota-reminder';
}) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.TrialToPaidModalOpened,
      location: params.location,
    },
  });

  // V2 — modal_viewed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
    },
  });
}

export function trialToPaidModalPeriodChanged(input: { period: Period }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.TrialToPaidModalPeriodChanged,
      period: input.period,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'period_changed',
      period: input.period,
    },
  });
}

export function trialToPaidModalUpgradeClicked(input: { period: Period }) {
  trackLegacyEvent({
    name: SegmentObjectName.ReverseTrial,
    props: {
      action: SegmentActionName.TrialToPaidModalUpgradeClicked,
      period: input.period,
    },
  });

  // V2 — button_clicked
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.UpgradeToProModal,
      label: 'upgrade',
      period: input.period,
    },
  });
}
